/**
 * Owner: Masa Bayu
 * File: typography-scale.ts
 * Date:Time: 2026-09-13 21:04
 * Description: 22B size, line-height, letter-spacing semantic scales.
 */
import { QX_DESIGN_SCALE_UNIT } from './design-spacing';
export const QX_TYPOGRAPHY_SIZE_STEPS = [
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
];
export const QX_TYPOGRAPHY_LINE_HEIGHT_STEPS = ['tight', 'snug', 'normal', 'relaxed'];
export const QX_TYPOGRAPHY_LETTER_SPACING_STEPS = ['tight', 'normal', 'wide'];
export function isQXTypographySizeStep(value) {
    return (typeof value === 'string' && QX_TYPOGRAPHY_SIZE_STEPS.includes(value));
}
export function isQXTypographyLineHeightStep(value) {
    return (typeof value === 'string' &&
        QX_TYPOGRAPHY_LINE_HEIGHT_STEPS.includes(value));
}
export function isQXTypographyLetterSpacingStep(value) {
    return (typeof value === 'string' &&
        QX_TYPOGRAPHY_LETTER_SPACING_STEPS.includes(value));
}
export function createQXTypographySizeToken(step, value) {
    if (!isQXTypographySizeStep(step)) {
        throw new Error(`Invalid typography size step: ${String(step)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new Error('Typography size value must be a positive finite number');
    }
    return Object.freeze({ step, value, unit: QX_DESIGN_SCALE_UNIT });
}
export function createQXTypographyLineHeightToken(step, value) {
    if (!isQXTypographyLineHeightStep(step)) {
        throw new Error(`Invalid typography line-height step: ${String(step)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new Error('Typography line-height value must be a positive finite number');
    }
    return Object.freeze({ step, value, unit: QX_DESIGN_SCALE_UNIT });
}
export function createQXTypographyLetterSpacingToken(step, value) {
    if (!isQXTypographyLetterSpacingStep(step)) {
        throw new Error(`Invalid typography letter-spacing step: ${String(step)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error('Typography letter-spacing value must be a finite number');
    }
    return Object.freeze({
        step,
        value: value === 0 ? 0 : value,
        unit: QX_DESIGN_SCALE_UNIT,
    });
}
function assertUniqueSteps(steps, label) {
    const seen = new Set();
    for (const step of steps) {
        if (seen.has(step)) {
            throw new Error(`Duplicate typography ${label} step: ${step}`);
        }
        seen.add(step);
    }
}
export function createQXTypographySizeScale(sizes) {
    const list = [...sizes];
    assertUniqueSteps(list.map((item) => item.step), 'size');
    for (const item of list) {
        if (!isQXTypographySizeStep(item.step)) {
            throw new Error(`Invalid typography size step: ${String(item.step)}`);
        }
    }
    return Object.freeze({ sizes: Object.freeze(list) });
}
export function createQXTypographyLineHeightScale(lineHeights) {
    const list = [...lineHeights];
    assertUniqueSteps(list.map((item) => item.step), 'line-height');
    for (const item of list) {
        if (!isQXTypographyLineHeightStep(item.step)) {
            throw new Error(`Invalid typography line-height step: ${String(item.step)}`);
        }
    }
    return Object.freeze({ lineHeights: Object.freeze(list) });
}
export function createQXTypographyLetterSpacingScale(letterSpacings) {
    const list = [...letterSpacings];
    assertUniqueSteps(list.map((item) => item.step), 'letter-spacing');
    for (const item of list) {
        if (!isQXTypographyLetterSpacingStep(item.step)) {
            throw new Error(`Invalid typography letter-spacing step: ${String(item.step)}`);
        }
    }
    return Object.freeze({ letterSpacings: Object.freeze(list) });
}
export function isQXTypographySizeScale(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    return Array.isArray(value.sizes);
}
export function isQXTypographyLineHeightScale(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    return Array.isArray(value.lineHeights);
}
export function isQXTypographyLetterSpacingScale(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    return Array.isArray(value.letterSpacings);
}
//# sourceMappingURL=typography-scale.js.map